import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	Link,
	TextField,
	Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import React from "react";
import { FreelancerActivityReportFormViewModel } from "../../../ViewModels/FreelancerActivityReportFormViewModel";
import { FreelancerActivityReportStatus } from "../../../models/enums/FreelancerActivityReportStatus";
import i18n from "../../../i18n/i18n";
import { zoullooColors } from "../../../theme";
import { Trans, useTranslation } from "react-i18next";
import {
	FreelancerActivityReport,
	ProposalTaskRateOption
} from "../../../models/FreelancerActivityReport";
import { UseMutationResult } from "@tanstack/react-query";
import { UpdateFreelancerActivityReportRequest } from "../../../models/requests/UpdateFreelancerActivityReportRequest";
import { BulkChangeFreelancerActivityReportStatusRequest } from "../../../models/requests/BulkChangeFreelancerActivityReportStatusRequest";

// Helper function to get the last working day of a month (excluding weekends)
const getLastWorkingDay = (year: number, month: number): dayjs.Dayjs => {
	let date = dayjs(new Date(year, month, 0)); // Last day of the month
	// If it's Saturday (6) or Sunday (0), go back to Friday
	while (date.day() === 0 || date.day() === 6) {
		date = date.subtract(1, "day");
	}
	return date;
};

// Get submission deadline: J-1 of last working day
const getSubmissionDeadline = (year: number, month: number): string => {
	const lastWorkingDay = getLastWorkingDay(year, month);
	const deadline = lastWorkingDay.subtract(1, "day");
	return deadline.format("DD/MM/YYYY");
};

// Get opening date: J-5 of last working day, except December (15/12) and August (10/08)
export const getOpeningDate = (year: number, month: number): string => {
	// Special cases for December and August
	if (month === 12) {
		return `15/12/${year}`;
	}
	if (month === 8) {
		return `10/08/${year}`;
	}

	const lastWorkingDay = getLastWorkingDay(year, month);
	const openingDate = lastWorkingDay.subtract(5, "day");
	return openingDate.format("DD/MM/YYYY");
};

// Validation schema will be created inside component to access t function
const createValidationSchema = (t: any) =>
	Yup.object().shape({
		startDate: Yup.date()
			.nullable()
			.when("noActivity", ([noActivity], schema) =>
				noActivity === false
					? schema.required(t("activityReports.validation.startDateRequired"))
					: schema.nullable()
			),
		endDate: Yup.date()
			.nullable()
			.when(["noActivity", "startDate"], ([noActivity], schema) =>
				noActivity === false
					? schema
							.required(t("activityReports.validation.endDateRequired"))
							.test(
								"is-after-start",
								t("activityReports.validation.endDateAfterStartOrEqual"),
								function (value) {
									const { startDate } = this.parent;
									if (!startDate || !value) return true;
									return (
										dayjs(value).isAfter(dayjs(startDate)) ||
										dayjs(value).isSame(dayjs(startDate))
									);
								}
							)
					: schema.nullable()
			),
		workedUnits: Yup.number().when("noActivity", ([noActivity], schema) =>
			noActivity === false
				? schema
						.required(t("activityReports.validation.workedUnitsRequired"))
						.positive(t("activityReports.validation.workedUnitsPositive"))
						.typeError(t("activityReports.validation.workedUnitsInvalid"))
				: schema
						.nullable()
						.transform((value, originalValue) => (originalValue === "" ? null : value))
		),
		averageRate: Yup.number().when("noActivity", ([noActivity], schema) =>
			noActivity === false
				? schema
						.required(t("activityReports.validation.rateRequired"))
						.positive(t("activityReports.validation.ratePositive"))
						.typeError(t("activityReports.validation.rateInvalid"))
				: schema
						.nullable()
						.transform((value, originalValue) => (originalValue === "" ? null : value))
		),
		totalReported: Yup.number()
			.nullable()
			.transform((value, originalValue) => (originalValue === "" ? null : value)),
		commentsOrQuestions: Yup.string()
			.max(2500, t("activityReports.validation.commentsMaxLength"))
			.nullable(),
		noActivity: Yup.boolean()
	});

interface ActivityReportFormProps {
	report: FreelancerActivityReport;
	contractualDays?: number;
	consumedDaysByOtherReports?: number;
	updateMutation: UseMutationResult<
		FreelancerActivityReport,
		Error,
		{
			id: number;
			request: UpdateFreelancerActivityReportRequest;
		},
		unknown
	>;
	bulkChangeStatusMutation: UseMutationResult<
		void,
		Error,
		BulkChangeFreelancerActivityReportStatusRequest[],
		unknown
	>;
}

const ActivityReportForm: React.FC<ActivityReportFormProps> = ({
	report,
	contractualDays,
	consumedDaysByOtherReports,
	updateMutation,
	bulkChangeStatusMutation
}) => {
	const { t } = useTranslation();

	// Helper function to limit decimal places
	const handleDecimalInput = (value: string, maxDecimals: number = 2): string => {
		if (!value) return value;
		const parts = value.split(".");
		if (parts.length === 2 && parts[1].length > maxDecimals) {
			return `${parts[0]}.${parts[1].slice(0, maxDecimals)}`;
		}
		return value;
	};
	const monthNames = [
		t("months.january"),
		t("months.february"),
		t("months.march"),
		t("months.april"),
		t("months.may"),
		t("months.june"),
		t("months.july"),
		t("months.august"),
		t("months.september"),
		t("months.october"),
		t("months.november"),
		t("months.december")
	];
	const monthName = monthNames[report.month - 1] || report.month;
	const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);

	const handleSaveAndFinishLater = async (
		values: FreelancerActivityReportFormViewModel
	) => {
		try {
			await updateMutation.mutateAsync({
				id: report.id,
				request: {
					startDate: values.startDate
						? dayjs(values.startDate).format("YYYY-MM-DD")
						: undefined,
					endDate: values.endDate
						? dayjs(values.endDate).format("YYYY-MM-DD")
						: undefined,
					workedUnits:
						typeof values.workedUnits === "string"
							? Number.parseFloat(values.workedUnits)
							: values.workedUnits,
					averageRate:
						typeof values.averageRate === "string"
							? Number.parseFloat(values.averageRate)
							: values.averageRate,
					commentsOrQuestions: values.commentsOrQuestions || undefined,
					noActivity: values.noActivity
				}
			});
		} catch (error) {
			console.error("Error updating report:", error);
		}
	};

	const validationSchema = React.useMemo(() => createValidationSchema(t), [t]);

	const formik = useFormik<FreelancerActivityReportFormViewModel>({
		initialValues: {
			startDate: report.startDate ? dayjs(report.startDate) : null,
			endDate: report.endDate ? dayjs(report.endDate) : null,
			workedUnits: report.workedUnits || "",
			averageRate: report.averageRate || "",
			totalReported: report.totalReported || "",
			commentsOrQuestions: report.commentsOrQuestions || "",
			noActivity: report.noActivity || false
		},
		validationSchema,
		enableReinitialize: true,
		onSubmit: handleSaveAndFinishLater
	});

	const {
		values,
		errors,
		touched,
		handleChange,
		handleBlur,
		setFieldValue,
		isSubmitting,
		handleSubmit
	} = formik;

	const isReadOnly = report.status === "Submitted";

	// Check if worked units exceed remaining contractual days
	const remainingDays =
		contractualDays != null && consumedDaysByOtherReports != null
			? contractualDays - consumedDaysByOtherReports
			: undefined;
	const currentWorkedUnits = values.workedUnits
		? parseFloat(String(values.workedUnits))
		: 0;
	const isOverLimit = remainingDays != null && currentWorkedUnits > remainingDays;

	// Auto-calculate totalReported when workedUnits or averageRate changes
	React.useEffect(() => {
		if (!values.noActivity && values.workedUnits && values.averageRate) {
			const units =
				typeof values.workedUnits === "string"
					? Number.parseFloat(values.workedUnits)
					: values.workedUnits;
			const rate =
				typeof values.averageRate === "string"
					? Number.parseFloat(values.averageRate)
					: values.averageRate;
			if (!Number.isNaN(units) && !Number.isNaN(rate)) {
				const newTotal = units * rate;
				const currentTotal =
					typeof values.totalReported === "string"
						? Number.parseFloat(values.totalReported)
						: values.totalReported;
				// Only update if the calculated value is different (avoid string/number mismatch)
				if (Number.isNaN(currentTotal) || Math.abs(newTotal - currentTotal) > 0.001) {
					setFieldValue("totalReported", newTotal);
				}
			}
		}
	}, [values.workedUnits, values.averageRate, values.noActivity, values.totalReported, setFieldValue]);

	// Clear fields when noActivity is checked (except averageRate if pre-filled from proposal)
	React.useEffect(() => {
		if (values.noActivity) {
			setFieldValue("startDate", null);
			setFieldValue("endDate", null);
			setFieldValue("workedUnits", "");
			// Only clear averageRate if it wasn't pre-filled from the proposal
			if (!report.averageRate) {
				setFieldValue("averageRate", "");
			}
			setFieldValue("totalReported", "");
		}
	}, [values.noActivity, setFieldValue, report.averageRate]);

	const handleSubmitReport = async () => {
		try {
			// First save the current form values
			if (formik.dirty) {
				await handleSaveAndFinishLater(formik.values);
			}

			// Then change status to Submitted
			await bulkChangeStatusMutation.mutateAsync([
				{
					id: report.id,
					status: FreelancerActivityReportStatus.Submitted
				}
			]);
			setOpenConfirmDialog(false);
		} catch (error) {
			console.error("Error submitting report:", error);
			setOpenConfirmDialog(false);
		}
	};

	return (
		<Accordion key={report.id} elevation={0}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					width="100%"
				>
					<Typography variant="h4">
						{monthName} {report.year}
					</Typography>
					<Box display="flex" alignItems="center" gap={1} mr={2}>
						{report.status === "Closed" && (
							<Chip
								label={t("FreelancerActivityReportStatuses.Closed")}
								size="medium"
								sx={{
									bgcolor: zoullooColors.error.main,
									color: zoullooColors.text.white,
									fontSize: "1rem",
									minWidth: "120px"
								}}
							/>
						)}
						{report.status === "Opened" && (
							<Chip
								label={t("FreelancerActivityReportStatuses.Opened")}
								size="medium"
								sx={{
									bgcolor: zoullooColors.info.main,
									color: zoullooColors.text.white,
									fontSize: "1rem",
									minWidth: "120px"
								}}
							/>
						)}
						{report.status === "Submitted" && (
							<Chip
								label={t("FreelancerActivityReportStatuses.Submitted")}
								size="medium"
								sx={{
									bgcolor: zoullooColors.success.main,
									color: zoullooColors.text.white,
									fontSize: "1rem",
									minWidth: "120px"
								}}
							/>
						)}
					</Box>
				</Box>
			</AccordionSummary>
			<AccordionDetails
				sx={{ borderTop: `1px solid ${zoullooColors.grey[400]}`, paddingX: 4 }}
			>
				{(report.status === "Opened" || report.status === "Submitted") && (
					<Box mt={2} component="form" onSubmit={handleSubmit}>
						{report.status === "Opened" && (
							<>
								<Alert severity="info">
									<Typography variant="body1">
										{t("activityReports.form.submitDeadline")}{" "}
										<Typography variant="h6" component={"span"}>
											{getSubmissionDeadline(report.year, report.month)}
										</Typography>
										.
									</Typography>
								</Alert>{" "}
								<Typography variant="body1" sx={{ mt: 3 }}>
									<Trans
										i18nKey={"activityReports.submitInfo.message1"}
										components={{
											strong: <strong />
										}}
									/>
								</Typography>
								<Grid container spacing={2} sx={{ my: 6 }}>
									<Grid size={{ xs: 6 }}>
										<Typography variant="subtitle1" gutterBottom>
											• {t("activityReports.form.instruction1")}
										</Typography>
										<Typography variant="subtitle1" gutterBottom>
											• {t("activityReports.form.instruction2")}
										</Typography>
									</Grid>
									<Grid size={{ xs: 6 }}>
										<Typography variant="subtitle1" gutterBottom>
											• {t("activityReports.form.instruction3")}
										</Typography>
										<Typography variant="subtitle1" gutterBottom>
											• {t("activityReports.form.instruction4")}
										</Typography>
									</Grid>
								</Grid>
							</>
						)}
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 4 }}>
								<LocalizationProvider
									dateAdapter={AdapterDayjs}
									adapterLocale={i18n.language}
								>
									<DatePicker
										label={t("activityReports.form.startDate")}
										views={["year", "month", "day"]}
										format="DD/MM/YYYY"
										value={values.startDate}
										onChange={value => setFieldValue("startDate", value)}
										disabled={values.noActivity || isReadOnly}
										slotProps={{
											textField: {
												fullWidth: true,
												variant: "outlined",
												error: touched.startDate && Boolean(errors.startDate),
												helperText: touched.startDate && errors.startDate,
												onBlur: handleBlur("startDate"),
												sx: {
													"& .MuiInputBase-input": {
														paddingLeft: 2.5
													},
													"& .MuiOutlinedInput-root": {
														height: "50px",
														"& fieldset": {
															borderColor: zoullooColors.grey[400]
														},
														"&:hover fieldset": {
															borderColor: zoullooColors.grey[700]
														},
														"&.Mui-focused fieldset": {
															borderColor: "#fcce03"
														}
													}
												}
											},
											yearButton: {
												sx: {
													"&.Mui-selected": {
														backgroundColor: `${zoullooColors.secondary.main} !important`,
														color: "#ffffff !important",
														"&:hover": {
															backgroundColor: `${
																zoullooColors.secondary.dark ||
																zoullooColors.secondary.main
															} !important`
														}
													}
												}
											},
											monthButton: {
												sx: {
													"&.Mui-selected": {
														backgroundColor: `${zoullooColors.secondary.main} !important`,
														color: "#ffffff !important",
														"&:hover": {
															backgroundColor: `${
																zoullooColors.secondary.dark ||
																zoullooColors.secondary.main
															} !important`
														}
													},
													"&.Mui-disabled": {
														backgroundColor: "#f5f5f5 !important",
														color: "#bdbdbd !important",
														cursor: "not-allowed !important",
														"&:hover": {
															backgroundColor: "#f5f5f5 !important"
														}
													}
												}
											},
											day: {
												sx: {
													"&.Mui-selected": {
														backgroundColor: `${zoullooColors.secondary.main} !important`,
														color: zoullooColors.secondary.contrastText,
														"&:hover": {
															backgroundColor: `${
																zoullooColors.secondary.dark ||
																zoullooColors.secondary.main
															} !important`
														}
													},
													"&.MuiButtonBase-root": {
														"&.MuiPickersDay-today": {
															backgroundColor: `${zoullooColors.secondary.light} !important`,
															color: zoullooColors.secondary.contrastText
														},
														"&:hover": {
															backgroundColor: `${zoullooColors.secondary.light} !important`,
															color: zoullooColors.secondary.contrastText
														}
													},
													"&.Mui-disabled": {
														backgroundColor: "#f5f5f5 !important",
														color: "#bdbdbd !important",
														cursor: "not-allowed !important",
														"&:hover": {
															backgroundColor: "#f5f5f5 !important"
														}
													}
												}
											}
										}}
									/>
								</LocalizationProvider>
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									type="number"
									label={
										report.rateType === "Hourly"
											? t("activityReports.form.workedHours")
											: t("activityReports.form.workedDays")
									}
									name="workedUnits"
									value={values.workedUnits}
									onChange={e => {
										const limitedValue = handleDecimalInput(e.target.value);
										setFieldValue("workedUnits", limitedValue);
									}}
									onBlur={handleBlur}
									disabled={values.noActivity || isReadOnly}
									error={
										(touched.workedUnits && Boolean(errors.workedUnits)) ||
										isOverLimit
									}
									helperText={
										(touched.workedUnits && errors.workedUnits) ||
										(isOverLimit
											? t("activityReports.validation.workedUnitsExceedsRemaining", {
													remaining: remainingDays
											  })
											: remainingDays != null
												? t("activityReports.validation.remainingDaysInfo", {
														remaining: remainingDays
												  })
												: undefined)
									}
									slotProps={{
										htmlInput: { step: 0.01 }
									}}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								{report.availableRates && report.availableRates.length > 0 ? (
									<Autocomplete<ProposalTaskRateOption, false, false, false>
										options={report.availableRates}
										getOptionLabel={(option: ProposalTaskRateOption) =>
											new Intl.NumberFormat("fr-FR", {
												style: "currency",
												currency: "EUR"
											}).format(option.unitPrice)
										}
										value={
											report.availableRates.find(
												rate => rate.unitPrice === values.averageRate
											) || null
										}
										onChange={(_, newValue: ProposalTaskRateOption | null) => {
											setFieldValue(
												"averageRate",
												newValue ? newValue.unitPrice : ""
											);
										}}
										disabled={values.noActivity || isReadOnly}
										isOptionEqualToValue={(
											option: ProposalTaskRateOption,
											value: ProposalTaskRateOption
										) => option.unitPrice === value.unitPrice}
										renderInput={params => (
											<TextField
												{...params}
												fullWidth
												label={
													report.rateType === "Hourly"
														? t("activityReports.form.hourlyRate")
														: t("activityReports.form.dailyRate")
												}
												error={touched.averageRate && Boolean(errors.averageRate)}
												helperText={touched.averageRate && errors.averageRate}
												onBlur={handleBlur("averageRate")}
											/>
										)}
									/>
								) : (
									<TextField
										fullWidth
										type="number"
										label={
											report.rateType === "Hourly"
												? t("activityReports.form.hourlyRate")
												: t("activityReports.form.dailyRate")
										}
										name="averageRate"
										value={values.averageRate}
										onChange={e => {
											const limitedValue = handleDecimalInput(e.target.value);
											setFieldValue("averageRate", limitedValue);
										}}
										onBlur={handleBlur}
										disabled={values.noActivity || isReadOnly}
										error={touched.averageRate && Boolean(errors.averageRate)}
										helperText={touched.averageRate && errors.averageRate}
										slotProps={{
											htmlInput: { step: 0.01 }
										}}
									/>
								)}
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								<LocalizationProvider
									dateAdapter={AdapterDayjs}
									adapterLocale={i18n.language}
								>
									<DatePicker
										label={t("activityReports.form.endDate")}
										views={["year", "month", "day"]}
										format="DD/MM/YYYY"
										value={values.endDate}
										onChange={value => setFieldValue("endDate", value)}
										disabled={values.noActivity || isReadOnly}
										slotProps={{
											textField: {
												fullWidth: true,
												variant: "outlined",
												error: touched.endDate && Boolean(errors.endDate),
												helperText: touched.endDate && errors.endDate,
												onBlur: handleBlur("endDate"),
												sx: {
													"& .MuiInputBase-input": {
														paddingLeft: 2.5
													},
													"& .MuiOutlinedInput-root": {
														height: "50px",
														"& fieldset": {
															borderColor: zoullooColors.grey[400]
														},
														"&:hover fieldset": {
															borderColor: zoullooColors.grey[700]
														},
														"&.Mui-focused fieldset": {
															borderColor: "#fcce03"
														}
													}
												}
											},
											yearButton: {
												sx: {
													"&.Mui-selected": {
														backgroundColor: `${zoullooColors.secondary.main} !important`,
														color: "#ffffff !important",
														"&:hover": {
															backgroundColor: `${
																zoullooColors.secondary.dark ||
																zoullooColors.secondary.main
															} !important`
														}
													}
												}
											},
											monthButton: {
												sx: {
													"&.Mui-selected": {
														backgroundColor: `${zoullooColors.secondary.main} !important`,
														color: "#ffffff !important",
														"&:hover": {
															backgroundColor: `${
																zoullooColors.secondary.dark ||
																zoullooColors.secondary.main
															} !important`
														}
													},
													"&.Mui-disabled": {
														backgroundColor: "#f5f5f5 !important",
														color: "#bdbdbd !important",
														cursor: "not-allowed !important",
														"&:hover": {
															backgroundColor: "#f5f5f5 !important"
														}
													}
												}
											},
											day: {
												sx: {
													"&.Mui-selected": {
														backgroundColor: `${zoullooColors.secondary.main} !important`,
														color: zoullooColors.secondary.contrastText,
														"&:hover": {
															backgroundColor: `${
																zoullooColors.secondary.dark ||
																zoullooColors.secondary.main
															} !important`
														}
													},
													"&.MuiButtonBase-root": {
														"&.MuiPickersDay-today": {
															backgroundColor: `${zoullooColors.secondary.light} !important`,
															color: zoullooColors.secondary.contrastText
														},
														"&:hover": {
															backgroundColor: `${zoullooColors.secondary.light} !important`,
															color: zoullooColors.secondary.contrastText
														}
													},
													"&.Mui-disabled": {
														backgroundColor: "#f5f5f5 !important",
														color: "#bdbdbd !important",
														cursor: "not-allowed !important",
														"&:hover": {
															backgroundColor: "#f5f5f5 !important"
														}
													}
												}
											}
										}}
									/>
								</LocalizationProvider>
							</Grid>
							<Grid size={{ xs: 12, sm: 4 }}></Grid>
							<Grid size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									label={t("activityReports.form.totalAmount")}
									name="totalReported"
									value={
										values.totalReported
											? new Intl.NumberFormat("fr-FR", {
													style: "currency",
													currency: "EUR",
													minimumFractionDigits: 2,
													maximumFractionDigits: 2
											  }).format(Number(values.totalReported))
											: ""
									}
									slotProps={{
										input: {
											readOnly: true
										}
									}}
									disabled={values.noActivity || isReadOnly}
								/>
							</Grid>
						</Grid>
						<Box my={3}>
							<FormControlLabel
								control={
									<Checkbox
										checked={values.noActivity}
										onChange={e => {
											const checked = e.target.checked;
											setFieldValue("noActivity", checked);
											if (checked) {
												setFieldValue("startDate", null);
												setFieldValue("endDate", null);
												setFieldValue("workedUnits", "");
												// Only clear averageRate if it wasn't pre-filled from the proposal
												if (!report.averageRate) {
													setFieldValue("averageRate", "");
												}
												setFieldValue("totalReported", "");
												setFieldValue("commentsOrQuestions", "");
											}
										}}
										disabled={isReadOnly}
										size="medium"
										color="secondary"
									/>
								}
								label={t("activityReports.form.noActivity", { month: monthName })}
							/>
						</Box>
						<TextField
							fullWidth
							multiline
							rows={10}
							label={t("activityReports.form.commentsLabel")}
							placeholder={t("activityReports.form.commentsPlaceholder")}
							name="commentsOrQuestions"
							value={values.commentsOrQuestions}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isReadOnly}
							error={touched.commentsOrQuestions && Boolean(errors.commentsOrQuestions)}
							helperText={
								(touched.commentsOrQuestions && errors.commentsOrQuestions) ||
								(report.status === "Opened" &&
									t("activityReports.form.charactersCount", {
										count: values.commentsOrQuestions.length
									}))
							}
							sx={{
								mb: 3,
								"& .MuiInputBase-root textarea": {
									resize: "vertical"
								}
							}}
						/>
						{report.status === "Opened" && (
							<>
								<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
									{t("activityReports.submitInfo.message2")}
								</Typography>
								<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
									<Trans
										i18nKey={"activityReports.submitInfo.message3"}
										components={{
											strong: <strong />,
											br: <br />,
											email: (
												<Link
													href="mailto:accounting_ap@zoulloo.com"
													sx={{
														color: zoullooColors.secondary.main,
														fontWeight: "500",
														textDecoration: "none",
														cursor: "pointer",
														"&:hover": {
															textDecoration: "underline"
														}
													}}
												>
													accounting_ap@zoulloo.com
												</Link>
											)
										}}
									/>
								</Typography>
								<Box sx={{ mb: 5 }}>
									<Typography variant="body1" sx={{ mb: 2 }}>
										<Trans
											i18nKey={"activityReports.submitInfo.guidelinesTitle"}
											components={{
												strong: <strong />
											}}
										/>
									</Typography>
									<Box component="ul" sx={{ pl: 3, m: 0 }}>
										<Typography
											component="li"
											variant="body1"
											color="text.secondary"
											sx={{ mb: 1.5 }}
										>
											<Trans
												i18nKey={"activityReports.submitInfo.invoiceDate"}
												components={{
													strong: <strong />
												}}
											/>
										</Typography>
										<Typography
											component="li"
											variant="body1"
											color="text.secondary"
											sx={{ mb: 1.5 }}
										>
											<Trans
												i18nKey={"activityReports.submitInfo.invoicingPerProject"}
												components={{
													strong: <strong />
												}}
											/>
										</Typography>
										<Typography
											component="li"
											variant="body1"
											color="text.secondary"
											sx={{ mb: 1.5 }}
										>
											<Trans
												i18nKey={"activityReports.submitInfo.projectNumber"}
												components={{
													strong: <strong />
												}}
											/>
										</Typography>
										<Typography component="li" variant="body1" color="text.secondary">
											<Trans
												i18nKey={"activityReports.submitInfo.invoiceSubmission"}
												components={{
													strong: <strong />,
													email: (
														<Link
															href="mailto:accounting_ap@zoulloo.com"
															sx={{
																color: zoullooColors.secondary.main,
																fontWeight: "500",
																textDecoration: "none",
																cursor: "pointer",
																"&:hover": {
																	textDecoration: "underline"
																}
															}}
														>
															accounting_ap@zoulloo.com
														</Link>
													)
												}}
											/>
										</Typography>
									</Box>
								</Box>

								<Box display="flex" gap={2}>
									<Button
										type="submit"
										variant="outlined"
										disabled={isSubmitting || isReadOnly || isOverLimit}
										sx={{
											borderColor: zoullooColors.secondary.main,
											color: zoullooColors.secondary.main,
											"&:hover": {
												borderColor: zoullooColors.secondary.dark,
												bgcolor: zoullooColors.secondary.alpha
											}
										}}
									>
										{t("activityReports.buttons.saveAndFinish")}
									</Button>
									<Button
										variant="contained"
										onClick={() => setOpenConfirmDialog(true)}
										disabled={
											isSubmitting ||
											isOverLimit ||
											!validationSchema.isValidSync(formik.values) ||
											isReadOnly
										}
										sx={{
											bgcolor: zoullooColors.secondary.main,
											"&:hover": {
												bgcolor: zoullooColors.secondary.dark
											}
										}}
									>
										{t("activityReports.buttons.submitReport")}
									</Button>
								</Box>
							</>
						)}
					</Box>
				)}

				{report.status === "Closed" && (
					<Box py={12} px={5}>
						<Alert severity="info">
							<Typography variant="body1">
								{t("activityReports.closedMessage")}{" "}
								<Typography variant="h6" component={"span"}>
									{getOpeningDate(report.year, report.month)}
								</Typography>
							</Typography>
						</Alert>
					</Box>
				)}
			</AccordionDetails>

			{/* Confirmation Dialog */}
			<Dialog
				open={openConfirmDialog}
				onClose={() => setOpenConfirmDialog(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("activityReports.confirmDialog.title")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t("activityReports.confirmDialog.content")}
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						onClick={() => setOpenConfirmDialog(false)}
						variant="outlined"
						sx={{
							borderColor: zoullooColors.grey[400],
							color: zoullooColors.text.primary
						}}
					>
						{t("activityReports.buttons.cancel")}
					</Button>
					<Button
						onClick={handleSubmitReport}
						variant="contained"
						disabled={bulkChangeStatusMutation.isPending}
						sx={{
							bgcolor: zoullooColors.secondary.main,
							"&:hover": {
								bgcolor: zoullooColors.secondary.dark
							}
						}}
					>
						{bulkChangeStatusMutation.isPending
							? t("activityReports.buttons.submitting")
							: t("activityReports.buttons.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
		</Accordion>
	);
};

export default ActivityReportForm;
